import { Checkbox, FormControlLabel } from '@material-ui/core'
import React from 'react'
import Auth from '../Auth'
import Form from '../Form'
import PageStatus from '../PageStatus'
import pageStatusStyles from '../PageStatus.module.css'
import ViewerMode from '../ViewerMode'
import baseStyles from './Base.module.css'
import styles from './OrdinateurPortable.module.css'

const { REACT_APP_API_URI } = process.env

const PATH = '/ordinateur-portable'

const subChecks = [
    { key: 'checkedSpecs', label: 'Vérifier les caractéristiques minimales de ma filière' },
    { key: 'checkedDevice', label: 'Choisir un ordinateur compatible ou commander via Poseidon' },
    { key: 'checkedOffice', label: 'Installer Microsoft Office 365' },
    { key: 'checkedVpn', label: 'Installer le client VPN' },
    { key: 'checkedReady', label: "Préparer mon ordinateur avant la journée d'accueil" },
]

export default ({ path }) => {
    const { form, setAttribute } = Form.useContainer()
    const { viewerMode } = ViewerMode.useContainer()
    const { token } = Auth.useContainer()
    const setAttributeWithPath = setAttribute(PATH)
    const slot = form[PATH] || {}

    const onSubCheckChange = (key) => (e) => {
        const updatedForm = setAttributeWithPath(key, e.target.checked)
        fetch(`${REACT_APP_API_URI}/forms`, {
            method: 'POST',
            body: JSON.stringify(updatedForm),
            headers: {
                authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
        })
    }

    return (
        <div className={baseStyles.root}>
            <h2>Ordinateur portable</h2>

            <PageStatus
                path={path}
                label="Mon ordinateur est prêt pour démarrer mes études selon la configuration indiquée ci-dessous."
            >
                <p className={pageStatusStyles.title}>Statut des actions à entreprendre</p>
                <div className={styles['control-labels']}>
                    {subChecks.map(({ key, label }) => (
                        <FormControlLabel
                            key={key}
                            control={
                                <Checkbox
                                    disabled={viewerMode}
                                    checked={!!slot[key]}
                                    onChange={onSubCheckChange(key)}
                                />
                            }
                            label={label}
                            className={pageStatusStyles.label}
                        />
                    ))}
                </div>
            </PageStatus>

            <section className={styles.section}>
                <span className={styles.eyebrow}>Étapes</span>
                <h2>Ce que vous devez faire</h2>

                <div className={styles['step-grid']}>
                    <article className={styles.step}>
                        <div className={styles['step-number']}>1</div>
                        <h3>Vérifier les caractéristiques de votre filière</h3>
                        <p>
                            Chaque étudiant·e de 1re année doit posséder un{' '}
                            <strong>ordinateur portable pour la rentrée académique</strong>.
                        </p>
                        <p>Les exigences minimales dépendent de votre filière.</p>
                        <p>
                            <a
                                href="https://heig-vd.ch/campus/service-informatique#futur-etudiant"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Caractéristiques techniques par filière
                            </a>
                        </p>
                    </article>

                    <article className={styles.step}>
                        <div className={styles['step-number']}>2</div>
                        <h3>Choisir ou commander votre ordinateur</h3>
                        <p>
                            Vous pouvez utiliser votre propre ordinateur portable s'il respecte les caractéristiques
                            minimales de votre filière.
                        </p>
                        <p>
                            La <strong>plateforme Poseidon</strong> reste recommandée pour choisir une machine
                            compatible.
                        </p>
                        <p>
                            <a href="https://heig.ch/poseidon" target="_blank" rel="noopener noreferrer">
                                Plateforme Poseidon
                            </a>
                        </p>
                    </article>

                    <article className={styles.step}>
                        <div className={styles['step-number']}>3</div>
                        <h3>Préparer le système d'exploitation</h3>
                        <p>
                            Le système standard pour suivre les cours à la HEIG-VD est{' '}
                            <strong>Windows 11</strong>.
                        </p>
                        <p>
                            <strong>macOS est supporté uniquement dans certaines filières.</strong>
                        </p>
                        <p>
                            Linux peut être utilisé dans certaines filières, néanmoins les services de base peuvent
                            ne pas fonctionner, comme les imprimantes, les accès réseaux ou certains outils Microsoft
                            365.
                        </p>
                    </article>

                    <article className={styles.step}>
                        <div className={styles['step-number']}>4</div>
                        <h3>Installer Microsoft Office 365</h3>
                        <p>
                            Installez Microsoft Office 365 avant la rentrée avec votre compte{' '}
                            <strong>8.8@hes-so.ch</strong>.
                        </p>
                        <p>Les applications incluent notamment Outlook, Word, Excel, PowerPoint et Teams.</p>
                        <p>
                            <a href="https://m365.cloud.microsoft" target="_blank" rel="noopener noreferrer">
                                Microsoft 365
                            </a>
                        </p>
                    </article>

                    <article className={styles.step}>
                        <div className={styles['step-number']}>5</div>
                        <h3>Installer le client VPN</h3>
                        <p>
                            Installez le client VPN avant la rentrée afin d’accéder aux ressources internes de la HEIG-VD.
                        </p>
                        <p>
                            Connectez-vous avec votre <strong>adresse e-mail HEIG-VD</strong> et votre mot de passe.
                        </p>
                        <p>
                            Le portail de connexion est :{' '}
                            <a href="https://vpn.heig-vd.ch" target="_blank" rel="noopener noreferrer">
                                VPN HEIG-VD
                            </a> (Le VPN fonctionne uniquement en Suisse).
                        </p>
                        <p>Vous trouverez plus d’information pour la configuration dans la FAQ.</p>
                    </article>

                    <article className={styles.step}>
                        <div className={styles['step-number']}>6</div>
                        <h3>Configurer le Wi-Fi sur site</h3>
                        <p>
                            La connexion au Wi-Fi se fera lors de la journée d'accueil du{' '}
                            <strong>14 septembre 2026</strong>.
                        </p>
                        <p>
                            Connectez-vous au réseau <strong>eduroam</strong> avec votre adresse e-mail HEIG-VD.
                        </p>
                    </article>
                </div>
            </section>

            <section className={styles.section}>
                <span className={styles.eyebrow}>Repères utiles</span>
                <h2>Choisir et préparer votre ordinateur</h2>

                <div className={styles.cards}>
                    <article className={styles.card}>
                        <span className={styles['card-label']}>Option 1</span>
                        <h3>Votre propre ordinateur</h3>
                        <p>
                            Vous pouvez utiliser votre propre ordinateur si sa configuration respecte les exigences
                            minimales de votre filière.
                        </p>
                        <ul>
                            <li>
                                <strong>Windows 11 recommandé</strong>, macOS et Linux uniquement lorsque la filière le
                                permet.
                            </li>
                            <li>
                                Mémoire vive, processeur, stockage et carte graphique selon les exigences de la
                                filière.
                            </li>
                            <li>
                                Espace disque disponible pour Microsoft Office 365, les mises à jour et les logiciels
                                d'étude.
                            </li>
                            <li>Nom d'ordinateur sans accent ni caractère spécial.</li>
                        </ul>
                    </article>

                    <article className={styles.card}>
                        <span className={styles['card-label']}>Option 2</span>
                        <h3>Ordinateur acheté via Poseidon</h3>
                        <p>
                            Un ordinateur acheté via Poseidon est recommandé, car les modèles proposés sont
                            sélectionnés pour correspondre aux exigences des études à la HEIG-VD.
                        </p>
                        <p>
                            Cette option permet aussi de bénéficier de services utiles en cas de panne, comme la
                            réparation sur site et, selon les cas, un ordinateur de prêt fourni par le Helpdesk.
                        </p>
                    </article>

                    <article className={`${styles.card} ${styles['card-full-row']}`}>
                        <span className={styles['card-label']}>À vérifier</span>
                        <h3>Compte Windows et nom de l'ordinateur</h3>
                        <p>
                            Lors de l'initialisation d'un nouveau PC, ne vous connectez pas à votre session Windows
                            avec votre compte Microsoft HES-SO.
                        </p>
                        <p>
                            Utilisez de préférence un <strong>compte Microsoft privé</strong> ou un{' '}
                            <strong>compte local</strong> si possible afin d'éviter des complications d'accès à votre
                            session Windows une fois vos études terminées.
                        </p>
                        <p>
                            Vérifiez aussi que le nom de l'ordinateur ne contient pas d'accent ni de caractère
                            spécial. Exemple : <code className={styles.code}>PC de Sebastien</code> doit être renommé
                            en <code className={styles.code}>PC-Sebastien</code>.
                        </p>
                    </article>
                </div>

                <div className={styles['callout-info']}>
                    <strong>En résumé :</strong>
                    <ul>
                        <li>
                            • Chaque étudiant·e de 1re année doit disposer d'un{' '}
                            <strong>ordinateur portable personnel</strong> pour la rentrée.
                        </li>
                        <li>• Les caractéristiques minimales dépendent de la filière.</li>
                        <li>
                            • <strong>Windows 11</strong> est le système standard recommandé et entièrement supporté.
                        </li>
                        <li>• macOS est supporté uniquement dans certaines filières.</li>
                        <li>
                            • Linux peut être utilisé dans certaines filières, mais certains services de base peuvent
                            ne pas fonctionner.
                        </li>
                        <li>
                            • Poséidon est une plateforme qui vous offre la possibilité d'acquérir un ordinateur
                            adapté, avec des prestations de service supplémentaires.
                        </li>
                        <li>• Microsoft 365 et le VPN doivent être installés avant la rentrée.</li>
                        <li>
                            • Le Wi-Fi sera configuré sur site le <strong>14 septembre 2026</strong>.
                        </li>
                    </ul>
                </div>
            </section>

            <section className={styles.section}>
                <span className={styles.eyebrow}>FAQ</span>
                <h2>Questions fréquentes</h2>

                <div className={styles.faq}>
                    <details className={styles['faq-item']}>
                        <summary>Dois-je obligatoirement acheter mon ordinateur via Poseidon ?</summary>
                        <p>
                            Non. Vous pouvez utiliser votre propre ordinateur s'il respecte les caractéristiques
                            minimales de votre filière.
                        </p>
                        <p>
                            Poséidon a l'avantage de vous proposer des modèles compatibles et validés selon les
                            exigences de votre filière. De plus, vous bénéficiez de services supplémentaires, comme
                            une garantie étendue, des réparations sur site par le constructeur et la possibilité
                            d'avoir un ordinateur équivalent en prêt par le Service Informatique de la HEIG-VD durant
                            le temps de réparation.
                        </p>
                    </details>

                    <details className={styles['faq-item']}>
                        <summary>Où trouver les procédures détaillées ?</summary>
                        <p>
                            Les procédures détaillées et les liens de téléchargement se trouvent sur l'intranet, dans
                            la rubrique destinée aux futur-e-s étudiant·es.
                        </p>
                        <p>
                            <a
                                href="https://intra.heig-vd.ch/services/informatique/Pages/Futur-e-etudiant-e.aspx"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Procédures pour futur-e étudiant·e sur l'intranet
                            </a>
                        </p>
                    </details>

                    <details className={styles['faq-item']}>
                        <summary>Que faire si mon ordinateur n'a pas Windows 11 ?</summary>
                        <p>Windows 11 est le système standard supporté par la HEIG-VD.</p>
                        <p>
                            Si votre ordinateur ne dispose pas de Windows 11, il est nécessaire de régler la situation
                            avant la rentrée. Le Helpdesk peut vous accompagner pour l'installation, mais celle-ci
                            doit être effectuée avant le début des cours.
                        </p>
                        <p>
                            En général, un ordinateur neuf compatible est déjà livré avec le système adéquat. Il n'y a
                            généralement pas de raison de remplacer le système installé sur la machine.
                        </p>
                    </details>

                    <details className={styles['faq-item']}>
                        <summary>Pourquoi le nom de mon ordinateur est-il important ?</summary>
                        <p>
                            Un nom d'ordinateur contenant des accents ou des caractères spéciaux peut créer des
                            problèmes lors de certaines configurations.
                        </p>
                        <p>
                            Avant la rentrée, vérifiez que le nom de votre ordinateur contient uniquement des lettres
                            non accentuées, des chiffres ou des tirets.
                        </p>
                    </details>

                    <details className={styles['faq-item']}>
                        <summary>Puis-je utiliser un Mac ?</summary>
                        <p>
                            macOS est supporté uniquement dans certaines filières, lorsque cela est indiqué dans les
                            caractéristiques techniques.
                        </p>
                        <p>
                            Dans les autres cas, Windows 11 est recommandé, car certains logiciels nécessaires aux
                            études ne sont pas disponibles ou ne sont pas entièrement supportés sur macOS.
                        </p>
                    </details>

                    <details className={styles['faq-item']}>
                        <summary>Puis-je utiliser Linux ?</summary>
                        <p>
                            Linux peut être utilisé dans certaines filières, mais il ne permet pas de garantir le bon
                            fonctionnement de tous les services de base.
                        </p>
                        <p>
                            Selon la filière et la configuration, les imprimantes, les accès réseaux, certains outils
                            Microsoft 365 ou d'autres services peuvent ne pas fonctionner correctement.
                        </p>
                    </details>
                </div>
            </section>

            <section className={styles.section}>
                <span className={styles.eyebrow}>Support</span>
                <h2>Besoin d'aide ?</h2>
                <p>
                    En cas de doute sur le choix de votre ordinateur, sa compatibilité ou les installations à
                    effectuer avant la rentrée, contactez le Helpdesk.
                </p>

                <div className={styles.actions}>
                    <a className={styles.button} href="mailto:helpdesk@heig-vd.ch">
                        024 557 63 33 - helpdesk@heig-vd.ch
                    </a>
                    <a
                        className={styles['button-secondary']}
                        href="https://heig-vd.ch/campus/service-informatique#futur-etudiant"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        Service informatique HEIG-VD
                    </a>
                </div>
            </section>
        </div>
    )
}
